import { sanitizeRut } from './sanitizeRut'

function isValidRut(rut) {
  if (!rut || typeof rut !== 'string') return false
  const cleanRut = rut.replace(/\./g, '').toUpperCase()
  const [number, dv] = cleanRut.split('-')
  if (!number || !dv) return false

  let s = 0
  let m = 2
  for (let i = number.length - 1; i >= 0; i--) {
    s += number[i] * m
    m = m === 7 ? 2 : m + 1
  }
  const expectedDv = 11 - (s % 11)
  const dvStr = expectedDv === 11 ? '0' : expectedDv === 10 ? 'K' : String(expectedDv)
  return dvStr === dv
}

// 🔹 Convierte la hoja Excel “Facturas” en documentos estructurados
const COLS = {
  fila: 'N° Fila',
  fecha_emision: 'fecha_emision',
  tipo_venta: 'tipo_venta (Contado: 1, Crédito: 2)',
  rut_receptor: 'rut_receptor',
  contacto_receptor: 'contacto_receptor',
  tipo_documento: 'tipo_documento',
  detalle_nombre: 'detalle_nombre',
  detalle_descripcion: 'detalle_descripcion',
  detalle_precio_unitario: 'detalle_precio_unitario',
  detalle_cantidad: 'detalle_cantidad',
  detalle_descuento: 'detalle_descuento (%)',
  // Referencias (opcional por línea de cabecera)
  referencia_tipo: 'referencia_tipo',
  referencia_folio: 'referencia_folio',
  referencia_fecha: 'referencia_fecha',
  referencia_razon: 'referencia_razon'
}

const REFERENCE_MAPPINGS = {
  'Orden de Compra': '801',
  '801': '801',
  'HES': 'HES',
  'Hoja Entrada Servicio': 'HES',
  'Factura de Exportación Electrónica': '110',
  '110': '110',
  'Nota de Débito de Exportación Electrónica': '111',
  '111': '111',
  'Nota de Crédito de Exportación Electrónica': '112',
  '112': '112',
  'Factura Electrónica': '33',
  '33': '33',
  'Factura Exenta Electrónica': '34',
  '34': '34',
  'Boleta Electrónica': '39',
  '39': '39',
  'Boleta Exenta Electrónica': '41',
  '41': '41',
  'Guía Despacho Electrónica': '52',
  '52': '52',
  'Nota de Débito Electrónica': '56',
  '56': '56',
  'Nota de Crédito Electrónica': '61',
  '61': '61',
  'Contrato': '803',
  'Nota de Pedido': '802',
  'Guía de Despacho': '52',
  'Resolución': 'SET',
  'Nota de Crédito': '61',
  'Nota de Débito': '56',
  'Declaración de Exportación': '101',
  'Proceso ChileCompra': '801'
}

function normalizeValue(value) {
  return typeof value === 'string' ? value.trim() : value
}

function formatExcelDate(value) {
  if (value === undefined || value === null || value === '') return undefined

  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }

  if (typeof value === 'number') {
    // Convierte número de serie de Excel a fecha YYYY-MM-DD
    const date = new Date(Math.round((value - 25569) * 86400 * 1000))
    return date.toISOString().split('T')[0]
  }

  return String(value).trim()
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function parseNumericValue(value) {
  if (!hasValue(value)) return undefined

  const parsed = Number(String(value).replace(/\s+/g, '').replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

function resolveRowNumber(row, index) {
  const explicitRow = Number(row?.[COLS.fila])
  return Number.isFinite(explicitRow) && explicitRow > 0 ? explicitRow : index + 2
}

function buildDetailFromRow(row, rowNumber) {
  const name = normalizeValue(row?.[COLS.detalle_nombre])
  const description = normalizeValue(row?.[COLS.detalle_descripcion])
  const qty = normalizeValue(row?.[COLS.detalle_cantidad])
  const price = normalizeValue(row?.[COLS.detalle_precio_unitario])
  const discount = normalizeValue(row?.[COLS.detalle_descuento])

  const hasDetailData = [name, description, qty, price, discount].some(hasValue)
  if (!hasDetailData) return { detail: null, errors: [] }

  const quantity = parseNumericValue(qty)
  const unitPrice = parseNumericValue(price)
  const discountValue =
    typeof discount === 'string' && discount.endsWith('%')
      ? parseNumericValue(discount.replace('%', ''))
      : parseNumericValue(discount)

  const errors = []

  if (!hasValue(name)) {
    errors.push(`Fila ${rowNumber}: falta el nombre del detalle`)
  }

  if (quantity === undefined || Number.isNaN(quantity) || quantity <= 0) {
    errors.push(`Fila ${rowNumber}: la cantidad debe ser mayor a 0`)
  }

  if (unitPrice === undefined || Number.isNaN(unitPrice) || unitPrice < 0) {
    errors.push(`Fila ${rowNumber}: el precio unitario es obligatorio`)
  }

  if (discount !== undefined && discount !== null && discount !== '' && Number.isNaN(discountValue)) {
    errors.push(`Fila ${rowNumber}: el descuento no es válido`)
  }

  return {
    detail: {
      item_name: name || '',
      item_description: description || '',
      quantity,
      unit_price: unitPrice,
      discount_percent: hasValue(discount) ? discountValue : undefined
    },
    errors
  }
}

function buildReferenceFromRow(row) {
  const typeLabel = normalizeValue(row?.[COLS.referencia_tipo])
  const folio = normalizeValue(row?.[COLS.referencia_folio])
  const date = formatExcelDate(row?.[COLS.referencia_fecha])
  const reason = normalizeValue(row?.[COLS.referencia_razon])

  const hasRefData = [typeLabel, folio, date, reason].some(hasValue)
  if (!hasRefData) return null

  // Mapeo de etiqueta a código (o usar el código directamente si ya es uno)
  const dteCode = REFERENCE_MAPPINGS[typeLabel] || typeLabel || ''

  return {
    reference_type: 1, // Por defecto 1 (referencia a documento)
    reference_folio: Number(folio) || undefined,
    reference_date: date || '',
    reference_reason: reason || '',
    reference_doc_type: String(dteCode),
    dte_type_code: String(dteCode)
  }
}

export function excelRowsToDocuments(rows) {
  const documents = []
  const errorDetails = []
  let currentDoc = null
  let currentDocErrors = []
  let currentDocRow = null

  const finalizeCurrentDoc = () => {
    if (!currentDoc) return

    const normalizedDetails = (currentDoc.details || [])
      .filter(it => it.item_name || it.unit_price || it.quantity)
      .map((it, index) => ({ ...it, line_number: index + 1 }))

    currentDoc.details = normalizedDetails

    const validationErrors = [...currentDocErrors]

    if (!hasValue(currentDoc.date_issued)) {
      validationErrors.push('Falta la fecha de emisión')
    } else {
      const today = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD
      if (currentDoc.date_issued < today) {
        validationErrors.push('La fecha debe ser la fecha actual o futura')
      }
    }

    if (!hasValue(currentDoc.document_receiver?.rut)) {
      validationErrors.push('Falta el RUT del receptor')
    } else if (!isValidRut(currentDoc.document_receiver.rut)) {
      validationErrors.push('RUT inválido')
    }

    if (!['33', '34'].includes(String(currentDoc.dte_type?.code))) {
      validationErrors.push(
        'Tipo de documento no permitido. Solo se admiten 33 y 34'
      )
    }

    if (normalizedDetails.length === 0) {
      validationErrors.push('Debes incluir al menos un detalle válido')
    }

    const uniqueErrors = Array.from(new Set(validationErrors))

    if (uniqueErrors.length > 0) {
      errorDetails.push({
        row: currentDocRow,
        receiver:
          currentDoc.document_receiver?.business_name ||
          currentDoc.document_receiver?.rut ||
          'Sin receptor',
        errors: uniqueErrors
      })
    } else {
      documents.push(currentDoc)
    }

    currentDoc = null
    currentDocErrors = []
    currentDocRow = null
  }

  rows.forEach((row, index) => {
    const rowNumber = resolveRowNumber(row, index)
    const hasHeader =
      hasValue(row?.[COLS.rut_receptor]) ||
      hasValue(row?.[COLS.fecha_emision]) ||
      hasValue(row?.[COLS.tipo_documento]) ||
      hasValue(row?.[COLS.tipo_venta])

    if (hasHeader) {
      finalizeCurrentDoc()

      const rutReceiver = sanitizeRut(normalizeValue(row?.[COLS.rut_receptor]) || '')

      currentDoc = {
        date_issued: formatExcelDate(row?.[COLS.fecha_emision]),
        dte_type: { code: String(normalizeValue(row?.[COLS.tipo_documento]) || '33') },
        header: { payment_method: String(normalizeValue(row?.[COLS.tipo_venta]) || '') },
        document_receiver: {
          rut: rutReceiver,
          business_name: normalizeValue(row?.['razon_social']) || '',
          address: normalizeValue(row?.['direccion']) || '',
          district: normalizeValue(row?.['comuna']) || '',
          city: normalizeValue(row?.['ciudad']) || '',
          email: normalizeValue(row?.[COLS.contacto_receptor]) || ''
        },
        details: [],
        references: []
      }

      currentDocRow = rowNumber

      const { detail, errors } = buildDetailFromRow(row, rowNumber)
      if (detail) currentDoc.details.push(detail)

      const reference = buildReferenceFromRow(row)
      if (reference) currentDoc.references.push(reference)

      currentDocErrors = errors
    } else if (currentDoc) {
      const { detail, errors } = buildDetailFromRow(row, rowNumber)
      if (detail) currentDoc.details.push(detail)
      currentDocErrors.push(...errors)
    }
  })

  finalizeCurrentDoc()

  return {
    documents,
    report: {
      processed: documents.length + errorDetails.length,
      loaded: documents.length,
      failed: errorDetails.length,
      errorDetails
    }
  }
}
